/**
 * ProfileHeader — шапка «Профиль» на странице Кошелёк.
 *
 *   Аватар · Имя @username · Ранг · Статус
 *   Личный объём · Оборот сети
 *   Дата регистрации · Реф-код (копируется)
 *   Иконка шестерёнки → открывает SettingsModal
 */
import { useCallback, useState } from 'react';
import { Settings, Copy, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { api } from '@/services/api';
import { useQuery } from '@tanstack/react-query';
import { GlassSurface } from '@/components/glass';
import { fmtNum, formatDateLocalized } from '@/lib/format';
import { useTelegramWebApp } from '@/hooks/useTelegramWebApp';
import { useUISounds } from '@/hooks/useUISounds';
import { SettingsModal } from './SettingsModal';

interface Profile {
  id: number;
  telegramId: string;
  username: string | null;
  firstName: string | null;
  lastName: string | null;
  photoUrl: string | null;
  referralCode: string;
  registeredAt: string;
  rank: { level: number; name: string | null };
  status: { level: number; name: string | null };
  personalVolume: string;
  networkTurnover: string;
}

export function ProfileHeader() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language === 'ru' ? 'ru' : 'en';
  const { haptic } = useTelegramWebApp();
  const sounds = useUISounds();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [imgFailed, setImgFailed] = useState(false);

  const { data } = useQuery<Profile | null>({
    queryKey: ['me-profile'],
    queryFn: async () => {
      const r = await api.get<{ success: boolean; profile: Profile }>('/me/profile');
      return r.data.profile;
    },
    staleTime: 60_000,
  });

  const copyRefCode = useCallback(() => {
    if (!data?.referralCode) return;
    haptic.selection();
    sounds.copySuccess();
    try {
      const shareLink = `https://t.me/moonraid_game_bot?start=moon_${data.referralCode}`;
      navigator.clipboard.writeText(shareLink);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      /* silent */
    }
  }, [data?.referralCode, haptic, sounds]);

  const displayName =
    data?.firstName?.trim() ||
    data?.username?.trim() ||
    `user#${data?.id ?? '?'}`;
  const avatarSrc = data?.telegramId
    ? `/api/luna/user/photo/${data.telegramId}`
    : data?.photoUrl || null;
  const initial = (data?.firstName ?? data?.username ?? '?').slice(0, 1).toUpperCase();

  return (
    <>
      <GlassSurface className="p-4 relative overflow-hidden">
        {/* Шестерёнка — настройки */}
        <button
          type="button"
          aria-label={t('profile.settingsAria')}
          onClick={() => { haptic.light(); sounds.bellOpen(); setSettingsOpen(true); }}
          className="absolute top-3 right-3 h-9 w-9 rounded-full flex items-center justify-center text-[var(--text-muted)] hover:text-[var(--text-primary)] transition"
          style={{ background: 'var(--glass-fill)', border: '1px solid var(--glass-stroke)' }}
        >
          <Settings size={15} strokeWidth={2.2} />
        </button>

        {/* Верх: аватар + имя + ранг/статус */}
        <div className="flex items-center gap-3 mb-4">
          <div
            className="relative flex-shrink-0 rounded-full overflow-hidden"
            style={{
              width: 56,
              height: 56,
              background: 'linear-gradient(135deg, var(--accent-primary) 0%, var(--accent-secondary) 100%)',
              border: '1.5px solid var(--glass-stroke-strong)',
            }}
          >
            {avatarSrc && !imgFailed ? (
              <img
                src={avatarSrc}
                alt=""
                className="w-full h-full object-cover"
                onError={() => setImgFailed(true)}
              />
            ) : null}
            <div
              className="absolute inset-0 flex items-center justify-center font-display text-[22px] font-extrabold text-[#0a0a0f]"
              style={{ zIndex: avatarSrc && !imgFailed ? -1 : 0 }}
            >
              {initial}
            </div>
          </div>
          <div className="min-w-0 flex-1 pr-10">
            <p className="font-display text-[15px] font-extrabold text-[var(--text-primary)] truncate">
              {displayName}
            </p>
            {data?.username && (
              <p className="text-[12px] text-[var(--text-muted)] truncate">
                @{data.username}
              </p>
            )}
            <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
              {data?.rank?.name ? (
                <span
                  className="text-[9px] uppercase tracking-[0.1em] font-mono px-2 py-0.5 rounded-full"
                  style={{
                    background: 'color-mix(in oklab, var(--accent-secondary) 18%, transparent)',
                    color: 'var(--accent-secondary)',
                    border: '1px solid color-mix(in oklab, var(--accent-secondary) 30%, transparent)',
                  }}
                >
                  R{data.rank.level} · {data.rank.name}
                </span>
              ) : null}
              {data?.status?.name ? (
                <span
                  className="text-[9px] uppercase tracking-[0.1em] font-mono px-2 py-0.5 rounded-full"
                  style={{
                    background: 'color-mix(in oklab, var(--accent-primary) 18%, transparent)',
                    color: 'var(--accent-primary)',
                    border: '1px solid color-mix(in oklab, var(--accent-primary) 30%, transparent)',
                  }}
                >
                  S{data.status.level} · {data.status.name}
                </span>
              ) : null}
            </div>
          </div>
        </div>

        {/* Метрики */}
        <div className="grid grid-cols-2 gap-2 mb-3">
          <MetricTile
            label={t('profile.personalVolume')}
            value={`${fmtNum(data?.personalVolume ?? '0')} USD₮`}
          />
          <MetricTile
            label={t('profile.networkTurnover')}
            value={`${fmtNum(data?.networkTurnover ?? '0')} USD₮`}
          />
        </div>

        {/* Низ: дата регистрации + реф-ссылка */}
        <div className="flex items-center justify-between gap-2 text-[11px] text-[var(--text-muted)]">
          <span className="flex-shrink-0">
            {data
              ? t('profile.registeredSince', { date: formatDateLocalized(data.registeredAt, lang) })
              : t('profile.registeredSince', { date: '—' })}
          </span>
          <button
            type="button"
            onClick={copyRefCode}
            title={data ? `t.me/moonraid_game_bot?start=moon_${data.referralCode}` : ''}
            className="inline-flex items-center gap-1.5 font-mono px-2.5 py-1.5 rounded-lg transition active:scale-[0.97] min-w-0 flex-1 justify-end"
            style={{
              background: 'var(--glass-fill)',
              border: '1px solid var(--glass-stroke)',
              color: 'var(--text-secondary)',
            }}
          >
            {copied ? <Check size={12} className="flex-shrink-0" /> : <Copy size={12} className="flex-shrink-0" />}
            <span className="truncate" style={{ direction: 'rtl', textAlign: 'left' }}>
              {data
                ? `t.me/moonraid_game_bot?start=moon_${data.referralCode}`
                : '—'}
            </span>
          </button>
        </div>
      </GlassSurface>

      <SettingsModal open={settingsOpen} onClose={() => setSettingsOpen(false)} />
    </>
  );
}

function MetricTile({ label, value }: { label: string; value: string }) {
  return (
    <div
      className="rounded-xl p-2.5"
      style={{
        background: 'var(--glass-fill)',
        border: '1px solid var(--glass-stroke)',
      }}
    >
      <p className="text-[9px] uppercase tracking-[0.1em] font-mono text-[var(--text-muted)] mb-0.5">
        {label}
      </p>
      <p className="font-display text-[14px] font-extrabold text-[var(--text-primary)] tabular-nums">
        {value}
      </p>
    </div>
  );
}
